import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';
import Flag from './Flag';

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches : false
    );
    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        const onChange = () => setIsMobile(mql.matches);
        onChange();
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, [breakpoint]);
    return isMobile;
}

export { useIsMobile };

function LanguageSwitcher({ variant = 'flags', className = '' }) {
    const { i18n } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

    const changeLang = (code) => {
        if (code !== current) i18n.changeLanguage(code);
    };

    if (variant === 'dropdown') {
        return <LanguageDropdown current={current} changeLang={changeLang} className={className} placement="bottom" />;
    }
    if (variant === 'dropdown-up') {
        return <LanguageDropdown current={current} changeLang={changeLang} className={className} placement="top" />;
    }
    if (variant === 'dropdown-up-md') {
        return <LanguageDropdown current={current} changeLang={changeLang} className={className} placement="top" size="md" />;
    }

    if (variant === 'compact') {
        return (
            <div className={`flex items-center gap-1 ${className}`}>
                {SUPPORTED_LANGUAGES.map((lng) => (
                    <button
                        key={lng.code}
                        onClick={() => changeLang(lng.code)}
                        title={lng.name}
                        className={`flex items-center gap-1.5 text-[10px] font-display uppercase tracking-widest px-2 py-1 rounded-md border transition-all duration-200 ${
                            current === lng.code
                                ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                                : 'border-cyber-border text-cyber-dim hover:text-cyber-text'
                        }`}
                    >
                        <Flag code={lng.code} />
                        <span>{lng.label}</span>
                    </button>
                ))}
            </div>
        );
    }

    // flags variant (large, for login)
    return (
        <div className={`flex items-center justify-center gap-3 ${className}`}>
            {SUPPORTED_LANGUAGES.map((lng) => (
                <button
                    key={lng.code}
                    onClick={() => changeLang(lng.code)}
                    title={lng.name}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 ${
                        current === lng.code
                            ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10 shadow-neon'
                            : 'border-cyber-border text-cyber-dim hover:text-cyber-text hover:border-cyber-border'
                    }`}
                >
                    <Flag code={lng.code} className="text-2xl" />
                    <span className="text-xs font-display tracking-widest">{lng.label}</span>
                </button>
            ))}
        </div>
    );
}

function LanguageDropdown({ current, changeLang, className, placement = 'bottom', size = 'sm' }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const isMobile = useIsMobile();
    const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === current) || SUPPORTED_LANGUAGES[0];
    const menuPos = placement === 'top'
        ? 'bottom-full mb-1'
        : 'top-full mt-1';
    const triggerSize = size === 'md'
        ? 'text-xs px-3 py-2 gap-2'
        : 'text-[10px] px-2 py-1.5 gap-1.5';

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (isMobile) return; // bottom sheet handles its own backdrop click
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, isMobile]);

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center font-display uppercase tracking-widest rounded-md border transition-all duration-200 ${triggerSize} ${
                    open
                        ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                        : 'border-cyber-border text-cyber-dim hover:text-cyber-text'
                }`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <Flag code={currentLang.code} />
                <span>{currentLang.label}</span>
                <span className={`text-[8px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {open && !isMobile && (
                <ul
                    role="listbox"
                    className={`absolute right-0 ${menuPos} z-50 min-w-[140px] neon-panel py-1 shadow-neon-strong ${
                        placement === 'top' ? 'animate-popup-pop-up' : 'animate-popup-pop'
                    }`}
                >
                    {SUPPORTED_LANGUAGES.map((lng) => {
                        const active = lng.code === current;
                        return (
                            <li key={lng.code}>
                                <button
                                    type="button"
                                    onClick={() => { changeLang(lng.code); setOpen(false); }}
                                    role="option"
                                    aria-selected={active}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-display tracking-widest text-left transition-colors ${
                                        active
                                            ? 'text-cyber-cyan bg-cyber-cyan/10'
                                            : 'text-cyber-dim hover:text-cyber-text hover:bg-cyber-border/30'
                                    }`}
                                >
                                    <Flag code={lng.code} />
                                    <span className="flex-1">{lng.name}</span>
                                    {active && <span className="text-cyber-cyan">✓</span>}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {open && isMobile && createPortal(
                <BottomSheet
                    title={t('common.language')}
                    onClose={() => setOpen(false)}
                    items={SUPPORTED_LANGUAGES.map((lng) => ({
                        key: lng.code,
                        active: lng.code === current,
                        onClick: () => { changeLang(lng.code); setOpen(false); },
                        leading: <Flag code={lng.code} className="text-2xl" />,
                        label: lng.name,
                    }))}
                />,
                document.body
            )}
        </div>
    );
}

const SHEET_EXIT_MS = 220;

export function BottomSheet({ title, items, onClose }) {
    const [closing, setClosing] = useState(false);

    const requestClose = () => {
        if (closing) return;
        setClosing(true);
        setTimeout(() => onClose && onClose(), SHEET_EXIT_MS);
    };

    const handleItemClick = (originalOnClick) => () => {
        if (closing || !originalOnClick) return;
        setClosing(true);
        setTimeout(() => originalOnClick(), SHEET_EXIT_MS);
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end"
            onClick={requestClose}
        >
            <div className={`absolute inset-0 bg-black/70 backdrop-blur-sm ${closing ? 'animate-sheet-fade-out' : 'animate-sheet-fade'}`} />
            <div
                className={`relative w-full neon-panel rounded-b-none rounded-t-xl p-3 pt-2 shadow-neon-strong flex flex-col max-h-[85vh] ${closing ? 'animate-sheet-slide-down' : 'animate-sheet-slide-up'}`}
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
                <div className="flex justify-center mb-2 shrink-0">
                    <span className="w-12 h-1 rounded-full bg-cyber-border" />
                </div>
                {title && (
                    <div className="neon-label text-center mb-3 shrink-0">{title}</div>
                )}
                <ul className="space-y-1 overflow-y-auto flex-1 min-h-0 -mx-1 px-1">
                    {items.map((it) => {
                        if (it.divider) {
                            return <li key={it.key} className="h-px bg-cyber-border/40 my-1.5" />;
                        }
                        const palette = it.danger
                            ? 'border-cyber-pink/40 text-cyber-pink active:bg-cyber-pink/10'
                            : it.active
                                ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                                : 'border-cyber-border text-cyber-dim active:bg-cyber-border/30';
                        return (
                            <li key={it.key}>
                                <button
                                    type="button"
                                    onClick={handleItemClick(it.onClick)}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-md border transition-colors text-left ${palette}`}
                                >
                                    {it.leading}
                                    <span className="flex-1 font-display tracking-widest text-sm">{it.label}</span>
                                    {it.active && <span className="text-cyber-cyan text-base">✓</span>}
                                </button>
                            </li>
                        );
                    })}
                </ul>
                <button
                    type="button"
                    onClick={requestClose}
                    className="neon-btn w-full mt-3 text-xs opacity-70 py-3 shrink-0"
                >
                    ✕
                </button>
            </div>
        </div>
    );
}

export default LanguageSwitcher;
